<script setup lang="ts">
import type {
	BehaviorConfig,
	NewSearchSuggestProps,
	StyleConfig,
	Suggestion,
	SuggestionCondoResult
} from './types';
import type { SuggestionGroupKey } from '~/composables/searchSuggest/types';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';

import {
	Command,
	CommandDialog,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/utils';
import {
	SECOND_FIELD_GROUP_ORDER,
	SECOND_FIELD_PROVIDERS,
	useGroupRegistry
} from '~/composables/searchSuggest/useGroupRegistry';
import { useModalLifecycle } from '~/composables/searchSuggest/useModalLifecycle';
import { usePlaceLocation } from '~/composables/searchSuggest/usePlaceLocation';
import { useSearchSuggestBackup } from '~/composables/searchSuggest/useSearchSuggestBackup';
import { useSuggestCore } from '~/composables/searchSuggest/useSuggestCore';
import { useSuggestionHandler } from '~/composables/searchSuggest/useSuggestionHandler';
import { useTypingActivity } from '~/composables/searchSuggest/useTypingActivity';
import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import { SEARCH_SUGGEST } from '~/constants';
import { VisuallyHidden } from 'reka-ui';

import IconMaterialSymbolsLightArrowLeftAlt from '~icons/material-symbols-light/arrow-left-alt';
import IconMaterialSymbolsLightCancel from '~icons/material-symbols-light/cancel';

import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import InsideLabelSuggestInput from './InsideLabelSuggestInput.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';

interface SecondFieldSuggestDialogProps extends NewSearchSuggestProps {
	placeholderClasses?: string;
	styleConfig?: StyleConfig;
	behaviorConfig?: BehaviorConfig;
	disabled?: boolean;
	hasFirstFieldValue?: boolean;
	firstFieldSearchTerm?: string;
	city?: string;
	state?: string;
	providers?: Array<
		| 'cities'
		| 'macroregions'
		| 'regions'
		| 'streets'
		| 'points_of_interest'
		| 'condos'
	>;
	groupOrder?: SuggestionGroupKey[];
}

const props = withDefaults(defineProps<SecondFieldSuggestDialogProps>(), {
	disabled: false,
	hasFirstFieldValue: false,
	firstFieldSearchTerm: '',
	isFetchingPropertyByCodeSearch: false,
	placeholders: () => ['Bairros, endereços e condomínios'],
	showInputButton: true,
	styleConfig: () => ({}),
	behaviorConfig: () => ({
		enableAnimation: false,
		closeOnClickOutside: true,
		closeOnSelect: true,
		showCloseIcon: false
	}),
	providers: () => SECOND_FIELD_PROVIDERS,
	groupOrder: () => SECOND_FIELD_GROUP_ORDER
});

const emit = defineEmits<{
	'select-neighborhood': [event: SelectAddressEvent];
	'select-street': [event: SelectAddressEvent];
	'select-poi': [event: SelectAddressEvent];
	'select-condo': [value: SuggestionCondoResult];
	'select-code': [value: Suggestion['searchParams']];
	'select-history': [value: Suggestion];
	'update:search-term': [value: string];
	'close-modal': [];
}>();

const isOpen = defineModel<boolean>('isOpen', { required: true });

const inputEl =
	useTemplateRef<InstanceType<typeof InsideLabelSuggestInput>>('inputEl');
const commandListEl = useTemplateRef<HTMLElement>('commandListEl');

const searchTerm = shallowRef<string>('');
const selectedValue = ref<SuggestionGroup>();
const hasSelectedValue = shallowRef<boolean>(false);

const {
	hasMinSearchTerm,
	locationAutocompleteData,
	loading,
	searchByCodeOption,
	executeLocationAutocomplete
} = useSuggestCore({
	searchTerm,
	providers: props.providers,
	hasSelectedValue,
	isDropdownOpen: isOpen
});

// Typing activity detection
const { isTyping } = useTypingActivity(
	searchTerm,
	SEARCH_SUGGEST.TYPING_DEBOUNCE_MS,
	hasSelectedValue
);

const { filteredLastResearchs, updateResearchs, removeResearchs } =
	useResearchsSuggest();

const registry = useGroupRegistry(props.providers);

const { getLocation } = usePlaceLocation();

const { keyboardHeight } = useModalLifecycle({
	isOpen,
	focusCallback: () => focusInput(),
	cleanupCallback: () => {}
});

const { extractBackup, restoreOrClearOnClose } = useSearchSuggestBackup({
	searchTerm: searchTerm as any,
	hasSelectedValue: hasSelectedValue as any
});

const isLoadingOrTyping = computed<boolean>(() => {
	return hasMinSearchTerm.value && (loading.value || isTyping.value);
});

const visibleGroups = computed(() => {
	if (props.disabled) return [];

	return registry.getVisibleGroups(
		{
			state: {
				searchTerm,
				loading,
				isDropdownOpened: isOpen,
				hasSelectedValue,
				locationAutocompleteData,
				filteredLastResearchs,
				searchByCodeOption,
				isTyping,
				disabled: toRef(() => props.disabled)
			},
			searchTerm: searchTerm.value,
			hasMinSearchTerm: hasMinSearchTerm.value
		},
		props.groupOrder
	);
});

const shouldShowNoResultsEmptyState = computed<boolean>(() => {
	return (
		!props.disabled &&
		hasMinSearchTerm.value &&
		!loading.value &&
		!isTyping.value &&
		isOpen.value &&
		(!locationAutocompleteData.value ||
			locationAutocompleteData.value.length === 0)
	);
});

const { handleSuggestion } = useSuggestionHandler(
	{
		emitHistory: (suggestion, isEmptyState) =>
			emit('select-history', suggestion),
		emitCode: searchParams => emit('select-code', searchParams),
		emitNeighborhood: event => emit('select-neighborhood', event),
		emitStreet: event => emit('select-street', event),
		emitPoi: event => emit('select-poi', event),
		emitCondo: value => emit('select-condo', value)
	},
	getLocation,
	shouldShowNoResultsEmptyState
);

const shouldShowInstructionalState = computed<boolean>(() => {
	// Verifica se há sugestões além do histórico de pesquisas
	const hasSearchSuggestions = visibleGroups.value.some(
		group =>
			group.key !== 'lastResearchs' &&
			group.suggestions &&
			group.suggestions.length > 0
	);

	return (
		!props.disabled &&
		!hasMinSearchTerm.value &&
		!loading.value &&
		isOpen.value &&
		!hasSearchSuggestions
	);
});

const hasHistorySuggestions = computed<boolean>(() => {
	return visibleGroups.value.some(
		group =>
			group.key === 'lastResearchs' &&
			group.suggestions &&
			group.suggestions.length > 0
	);
});

const defaultPlaceholder = 'Bairros, endereços e condomínios';
const openDialogPlaceholder =
	'Bairros, endereços, condomínios e código do imóvel';

const isTooltipEnabled = computed<boolean>(() => !props.disabled);
const isTooltipVisible = ref<boolean>(false);

function showTooltip() {
	isTooltipVisible.value = true;
}

function handleInputButtonClick() {
	if (props.disabled) {
		showTooltip();
		return;
	}

	extractBackup();

	// Defer heavy modal rendering to next frame to reduce input delay (INP optimization)
	requestAnimationFrame(() => {
		isOpen.value = true;
		hasSelectedValue.value = false;
		if (hasMinSearchTerm.value) {
			executeLocationAutocomplete();
		}
	});
}

function closeDialog() {
	restoreOrClearOnClose(true);

	isOpen.value = false;
	if (props.behaviorConfig?.closeOnSelect) {
		emit('close-modal');
	}
}

async function handleModelValueUpdate(suggestion: Suggestion) {
	hasSelectedValue.value = true;

	await handleSuggestion(suggestion, {
		searchTerm,
		closeOnSelect: props.behaviorConfig?.closeOnSelect,
		city: props.city,
		state: props.state,
		onClose: () => closeDialog(),
		onUpdateResearch: updateResearchs
	});
}

function handleInputClear() {
	searchTerm.value = '';
	hasSelectedValue.value = false;
	focusInput();
	isOpen.value = true;
}

function handleEnter(_: KeyboardEvent, selectedElement?: HTMLElement) {
	if (
		shouldShowNoResultsEmptyState.value &&
		hasMinSearchTerm.value &&
		!isTyping.value
	) {
		searchTerm.value = '';
		hasSelectedValue.value = false;
		return;
	}
	selectedElement?.click();
}

function focusInput() {
	if (!props.disabled) {
		inputEl.value?.focus();
	}
}

watch(
	() => props.isFetchingPropertyByCodeSearch,
	isFetching => {
		if (!isFetching) closeDialog();
	}
);

watch(searchTerm, newValue => {
	emit('update:search-term', newValue);
});

watch(isOpen, (newValue, oldValue) => {
	if (oldValue && !newValue) {
		nextTick(() => {
			if (!newValue) emit('close-modal');
		});
	}
});
</script>

<template>
	<div class="relative h-full max-w-full overflow-hidden">
		<!-- Trigger Button -->
		<TooltipProvider
			:disabled="isTooltipEnabled"
			disable-closing-trigger
			:delay-duration="100"
		>
			<Tooltip v-model:open="isTooltipVisible">
				<TooltipTrigger as-child>
					<div
						v-if="showInputButton"
						:class="cn('flex size-full items-center', inputWrapperClasses)"
					>
						<slot name="input-prefix" />
						<button
							:class="
								cn(
									'flex h-[50px] w-full items-center justify-between rounded-bl-[2px] rounded-br-[2px] px-4 py-2 text-sm font-normal leading-[1.5]',
									buttonInputClasses || 'bg-white',
									disabled && '!bg-white-dark-2 !cursor-not-allowed',
									hasFirstFieldValue && !disabled && 'bg-white'
								)
							"
							@click="handleInputButtonClick"
						>
							<span
								:class="
									cn(
										'flex-1 truncate text-left',
										disabled
											? 'text-black/40'
											: searchTerm
												? 'text-primary'
												: 'text-black/50',
										placeholderClasses
									)
								"
							>
								{{
									disabled || !isOpen
										? defaultPlaceholder
										: searchTerm || openDialogPlaceholder
								}}
							</span>
							<div
								:class="
									cn(
										'flex h-full w-7 items-center justify-end',
										inputClearIconWrapperClasses
									)
								"
							>
								<IconMaterialSymbolsLightCancel
									v-show="searchTerm && !disabled"
									:class="
										cn(
											'size-5 min-w-5 cursor-pointer text-white-dark-3',
											inputClearIconClasses
										)
									"
									@click.stop="handleInputClear"
								/>
							</div>
						</button>
					</div>
				</TooltipTrigger>
				<TooltipContent
					side="bottom"
					align="center"
					class="relative overflow-visible rounded-[0.125rem] bg-primary-light-4 p-3 font-sans text-xs font-normal leading-normal text-white before:absolute before:left-1/2 before:top-0 before:z-[100] before:size-1.5 before:-translate-y-1/2 before:translate-x-1/2 before:rotate-45 before:bg-inherit before:content-['']"
					:side-offset="-7"
				>
					Selecione primeiro uma cidade ou região
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>

		<!-- Modal -->
		<Transition name="slide-up">
			<div
				v-if="isOpen && !disabled"
				class="absolute left-0 top-0 h-dvh w-dvw max-w-none"
			>
				<CommandDialog
					:open="isOpen"
					class="h-dvh w-dvw max-w-none"
					:show-close-icon="false"
					title="Buscar por bairro, endereço, condomínio ou código do imóvel"
					hide-title
					@update:open="isOpen = $event"
				>
					<VisuallyHidden>
						<DialogTitle>{{ openDialogPlaceholder }}</DialogTitle>
					</VisuallyHidden>
					<VisuallyHidden>
						<DialogDescription />
					</VisuallyHidden>
					<div class="h-dvh w-dvw bg-white">
						<Command
							class="h-dvh w-full bg-white text-primary"
							:disabled="disabled"
							@update:model-value="
								(value: any) => handleModelValueUpdate(value)
							"
						>
							<InsideLabelSuggestInput
								ref="inputEl"
								v-model="searchTerm"
								:placeholder="openDialogPlaceholder"
								:floating-label="false"
								:class="
									cn(
										'h-[62px] text-ellipsis pl-[0.625rem] text-primary !text-base placeholder:text-transparent',
										inputClasses
									)
								"
								:auto-focus="true"
								:enable-animation="false"
								:show-close-icon="false"
								:disabled="disabled"
								@clear="handleInputClear"
								@enter="handleEnter"
							>
								<template #prefix>
									<div class="ml-5 flex size-6 items-center justify-center">
										<IconMaterialSymbolsLightArrowLeftAlt
											class="size-full !h-6 !w-6 text-primary"
											@click="closeDialog"
										/>
									</div>
									<div
										v-if="!searchTerm"
										class="pointer-events-none relative flex h-full w-0 items-center overflow-visible"
									>
										<span
											class="absolute left-0 top-1/2 w-[calc(100dvw-6rem)] translate-y-[-40%] truncate pl-2.5 text-base text-black/50"
										>
											{{ openDialogPlaceholder }}
										</span>
									</div>
								</template>
							</InsideLabelSuggestInput>
							<CommandSeparator class="bg-white-dark-2" />

							<CommandList
								ref="commandListEl"
								:class="
									cn(
										'h-full max-h-none border border-t-0 border-white-dark-2 bg-white p-0 transition-all duration-300 ease-out [&>div]:h-[calc(100%-62px)]',
										isLoadingOrTyping ? '!overflow-hidden' : 'overflow-y-auto'
									)
								"
								:style="{ paddingBottom: `${keyboardHeight + 16}px` }"
								data-scroll-area
							>
								<!-- Instructional empty state -->
								<div
									v-show="shouldShowInstructionalState"
									:class="
										cn(
											'flex flex-col items-center justify-center gap-1 px-5 py-8 text-center',
											!hasHistorySuggestions && 'h-full'
										)
									"
								>
									<h3
										class="font-matter-sq text-lg font-normal leading-[150%] tracking-[-0.0225rem] text-primary"
									>
										Encontre lugares em
										{{ firstFieldSearchTerm || 'sua região' }}
									</h3>
									<p
										class="font-sans text-xs font-normal leading-[150%] text-primary-light-3"
									>
										Digite bairro, endereço, condomínio ou código do imóvel para
										ver sugestões
									</p>
								</div>

								<!-- No results empty state -->
								<div
									v-show="shouldShowNoResultsEmptyState && !isTyping"
									class="px-5 py-6"
								>
									<ListingEmptyState
										:show-badges="false"
										icon-wrapper-classes="size-[47.5px]"
										icon-classes="size-[22.5px]"
										title-classes="text-lg"
										description-classes="text-sm"
									>
										<template #title>Nenhum resultado encontrado</template>
										<template #description>
											Tente uma busca diferente ou verifique a grafia
										</template>
									</ListingEmptyState>
								</div>

								<!-- Loading skeleton groups -->
								<LoadingGroup
									v-for="index in 3"
									:key="`loading-group-${index}`"
									:active="isLoadingOrTyping"
								/>

								<!-- Suggestion groups -->
								<template v-if="!isLoadingOrTyping && visibleGroups.length">
									<template
										v-for="(suggestionGroup, index) in visibleGroups"
										:key="`suggestion-group-${suggestionGroup.key}`"
									>
										<CommandSeparator
											class="bg-white-dark-2"
											:class="{
												hidden: index === 0
											}"
										/>

										<!-- History group -->
										<NewGroup
											v-if="
												suggestionGroup.key === 'lastResearchs' &&
												!shouldShowNoResultsEmptyState
											"
											suggestion-key="lastResearchs"
											:heading="suggestionGroup.heading"
											:suggestions="suggestionGroup.suggestions"
											:search-term="searchTerm"
											is-removable
											@remove-suggestion="removeResearchs"
										/>

										<!-- Other suggestion groups -->
										<NewGroup
											v-else-if="suggestionGroup.key !== 'code'"
											:suggestion-key="suggestionGroup.key"
											:heading="suggestionGroup.heading"
											:suggestions="suggestionGroup.suggestions"
											:search-term="searchTerm"
										/>

										<!-- Code group -->
										<CodeGroup
											v-else-if="suggestionGroup.key === 'code'"
											:search-term="searchTerm"
											suggestion-key="code"
											:suggestions="suggestionGroup.suggestions as Suggestion[]"
											:is-fetching-property-by-code-search="
												isFetchingPropertyByCodeSearch
											"
										/>
									</template>
								</template>
							</CommandList>
						</Command>
					</div>
				</CommandDialog>
			</div>
		</Transition>
	</div>
</template>
<!-- Styles moved to app/assets/css/main.css to avoid render-blocking CSS -->
